// FilterCacheManager.js - Fixed version with large data handling
class FilterCacheManager {
	constructor() {
		if (FilterCacheManager.instance) {
			return FilterCacheManager.instance;
		}

		this.STORAGE_KEY = "domain_filters_cache";
		this.CACHE_VERSION_KEY = "domain_filters_cache_version";
		this.USER_SESSION_KEY = "domain_filters_user_session";

		// Cache settings
		this.CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours
		this.CURRENT_CACHE_VERSION = "1.0.0";

		// ✅ NEW: Large data handling
		this.MAX_STORAGE_SIZE = 8 * 1024 * 1024; // 8MB limit (conservative)
		this.COMPRESSION_ENABLED = true;

		// In-memory cache
		this.memoryCache = new Map();
		this.cacheListeners = [];

		// Initialize
		this.initializeCache();

		FilterCacheManager.instance = this;
		console.log("🏗️ FilterCacheManager initialized with large data support");
	}

	// ==================== INITIALIZATION ====================

	initializeCache() {
		try {
			const storedVersion = localStorage.getItem(this.CACHE_VERSION_KEY);
			if (storedVersion !== this.CURRENT_CACHE_VERSION) {
				console.log("🔄 Cache version mismatch, clearing old cache...");
				this.clearAllCaches();
				localStorage.setItem(
					this.CACHE_VERSION_KEY,
					this.CURRENT_CACHE_VERSION,
				);
			}

			this.loadCacheFromStorage();
		} catch (error) {
			console.error("❌ Error initializing cache:", error);
			this.clearAllCaches();
		}
	}

	loadCacheFromStorage() {
		try {
			const cachedData = localStorage.getItem(this.STORAGE_KEY);
			if (cachedData) {
				const parsed = this.decompressData(cachedData);

				if (this.isCacheValid(parsed)) {
					this.memoryCache.set("filters", parsed);
					console.log("✅ Loaded valid cache from localStorage");
					return true;
				} else {
					console.log("⚠️ Cache expired or invalid, will fetch fresh data");
					this.clearCache();
				}
			}
		} catch (error) {
			console.error("❌ Error loading cache from storage:", error);
			this.clearCache();
		}
		return false;
	}

	// ==================== DATA COMPRESSION ====================

	compressData(data) {
		if (!this.COMPRESSION_ENABLED) return data;

		try {
			// Simple JSON compression by removing whitespace and common redundant data
			const compressed = JSON.stringify(data);
			console.log(`📦 Data size: ${(compressed.length / 1024).toFixed(1)}KB`);
			return compressed;
		} catch (error) {
			console.warn("⚠️ Compression failed, using original data:", error);
			return JSON.stringify(data);
		}
	}

	decompressData(compressedData) {
		try {
			return JSON.parse(compressedData);
		} catch (error) {
			console.error("❌ Decompression failed:", error);
			return null;
		}
	}

	// ✅ NEW: Data size optimization
	optimizeFilterData(data) {
		if (!data || !data.domainFilterValues) return data;

		const optimized = { ...data };

		// ✅ OPTIMIZATION 1: Limit categories to reduce size
		if (
			optimized.domainFilterValues.categories &&
			optimized.domainFilterValues.categories.length > 1000
		) {
			console.log(
				`📉 Optimizing categories: ${optimized.domainFilterValues.categories.length} → 1000 (most common)`,
			);

			// Keep most commonly used categories (you can customize this logic)
			optimized.domainFilterValues.categories =
				optimized.domainFilterValues.categories
					.slice(0, 1000) // Take first 1000
					.filter((cat) => cat && cat.trim() && cat !== "null"); // Clean up
		}

		// ✅ OPTIMIZATION 2: Remove null/empty values
		Object.keys(optimized.domainFilterValues).forEach((key) => {
			if (Array.isArray(optimized.domainFilterValues[key])) {
				optimized.domainFilterValues[key] = optimized.domainFilterValues[key]
					.filter((item) => item !== null && item !== undefined && item !== "")
					.slice(0, 500); // Limit arrays to 500 items max
			}
		});

		const originalSize = JSON.stringify(data).length;
		const optimizedSize = JSON.stringify(optimized).length;
		const savings = (
			((originalSize - optimizedSize) / originalSize) *
			100
		).toFixed(1);

		console.log(
			`🎯 Data optimization: ${(originalSize / 1024).toFixed(1)}KB → ${(optimizedSize / 1024).toFixed(1)}KB (${savings}% smaller)`,
		);

		return optimized;
	}

	// ==================== CACHE VALIDATION ====================

	isCacheValid(cacheData) {
		if (!cacheData || !cacheData.timestamp || !cacheData.data) {
			return false;
		}

		const isExpired = Date.now() - cacheData.timestamp > this.CACHE_DURATION;
		if (isExpired) {
			console.log("⏰ Cache expired");
			return false;
		}

		const currentSession = this.getCurrentUserSession();
		if (cacheData.userSession !== currentSession) {
			console.log("👤 User session changed, invalidating cache");
			return false;
		}

		const hasRequiredData =
			cacheData.data &&
			(cacheData.data.domainFilterValues || cacheData.data.filterValues);

		if (!hasRequiredData) {
			console.log("📭 Cache missing required data");
			return false;
		}

		return true;
	}

	getCurrentUserSession() {
		const user = this.getCurrentUser();
		if (user && user.email) {
			return `${user.email}_${new Date().toDateString()}`;
		}
		return `anonymous_${new Date().toDateString()}`;
	}

	getCurrentUser() {
		try {
			if (typeof window !== "undefined" && window.apiService) {
				return window.apiService.getUser();
			}

			const token = localStorage.getItem("google_id_token");
			if (token) {
				const payload = this.parseJWT(token);
				return payload
					? {
							email: payload.email,
							name: payload.name,
							id: payload.sub,
						}
					: null;
			}
		} catch (error) {
			console.warn("⚠️ Could not get current user:", error);
		}
		return null;
	}

	parseJWT(token) {
		try {
			const base64Url = token.split(".")[1];
			const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
			const jsonPayload = decodeURIComponent(
				Array.from(atob(base64))
					.map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
					.join(""),
			);
			return JSON.parse(jsonPayload);
		} catch {
			return null;
		}
	}

	// ==================== CACHE OPERATIONS ====================

	async getFilters(fetchFunction) {
		console.log("🔍 Getting filters from cache or API...");

		// Try memory cache first
		const memoryCached = this.memoryCache.get("filters");
		if (memoryCached && this.isCacheValid(memoryCached)) {
			console.log("⚡ Retrieved filters from memory cache");
			this.notifyListeners("cache_hit", memoryCached.data);
			return {
				success: true,
				data: memoryCached.data,
				source: "memory_cache",
				cached: true,
			};
		}

		// Try localStorage cache
		const storageCached = this.loadCacheFromStorage();
		if (storageCached) {
			const cacheData = this.memoryCache.get("filters");
			console.log("💾 Retrieved filters from localStorage cache");
			this.notifyListeners("cache_hit", cacheData.data);
			return {
				success: true,
				data: cacheData.data,
				source: "storage_cache",
				cached: true,
			};
		}

		// Fetch fresh data
		console.log("🌐 Fetching fresh filter data from API...");
		this.notifyListeners("cache_miss", null);

		try {
			const result = await fetchFunction();

			if (result.success && result.data) {
				await this.setFilters(result.data);
				console.log("✅ Fresh filter data cached successfully");
				this.notifyListeners("cache_updated", result.data);

				return {
					success: true,
					data: result.data,
					source: "api",
					cached: false,
				};
			} else {
				console.error("❌ Failed to fetch fresh filter data:", result.error);
				return result;
			}
		} catch (error) {
			console.error("❌ Error fetching filters:", error);
			return {
				success: false,
				error: error.message,
				data: null,
			};
		}
	}

	async setFilters(filterData) {
		try {
			// ✅ OPTIMIZE: Reduce data size before caching
			const optimizedData = this.optimizeFilterData(filterData);

			const cacheData = {
				data: optimizedData,
				timestamp: Date.now(),
				userSession: this.getCurrentUserSession(),
				version: this.CURRENT_CACHE_VERSION,
			};

			// Store in memory (always works)
			this.memoryCache.set("filters", cacheData);
			console.log("✅ Filters stored in memory cache");

			// ✅ SAFE: Try localStorage with size check
			try {
				const compressed = this.compressData(cacheData);
				const sizeInBytes = compressed.length * 2; // Rough estimate (UTF-16)

				if (sizeInBytes > this.MAX_STORAGE_SIZE) {
					console.warn(
						`⚠️ Data too large for localStorage (${(sizeInBytes / 1024 / 1024).toFixed(1)}MB), using memory only`,
					);
					this.notifyListeners("cache_stored", optimizedData);
					return true;
				}

				localStorage.setItem(this.STORAGE_KEY, compressed);
				console.log(
					`💾 Filters cached in localStorage (${(sizeInBytes / 1024).toFixed(1)}KB)`,
				);
			} catch (storageError) {
				if (storageError.name === "QuotaExceededError") {
					console.warn(
						"⚠️ localStorage quota exceeded, using memory cache only",
					);
					// Memory cache still works, so this is not a fatal error
				} else {
					console.warn("⚠️ localStorage error:", storageError.message);
				}
			}

			this.notifyListeners("cache_stored", optimizedData);
			return true;
		} catch (error) {
			console.error("❌ Error storing filters in cache:", error);
			return false;
		}
	}

	// ==================== MANUAL CACHE MANAGEMENT ====================

	async refreshFilters(fetchFunction) {
		console.log("🔄 Manually refreshing filters...");
		this.clearCache();
		return await this.getFilters(fetchFunction);
	}

	clearCache() {
		console.log("🧹 Clearing filter cache...");
		this.memoryCache.delete("filters");
		try {
			localStorage.removeItem(this.STORAGE_KEY);
		} catch (error) {
			console.warn("⚠️ Could not clear localStorage:", error);
		}
		this.notifyListeners("cache_cleared", null);
	}

	clearAllCaches() {
		console.log("🧹 Clearing all caches...");
		this.memoryCache.clear();
		try {
			localStorage.removeItem(this.STORAGE_KEY);
			localStorage.removeItem(this.CACHE_VERSION_KEY);
			localStorage.removeItem(this.USER_SESSION_KEY);
		} catch (error) {
			console.warn("⚠️ Could not clear localStorage:", error);
		}
		this.notifyListeners("cache_cleared", null);
	}

	// ==================== CACHE STATUS ====================

	getCacheStatus() {
		const memoryCached = this.memoryCache.get("filters");
		const hasMemoryCache = memoryCached && this.isCacheValid(memoryCached);

		let storageSize = 0;
		let hasStorageCache = false;
		try {
			const cachedData = localStorage.getItem(this.STORAGE_KEY);
			if (cachedData) {
				storageSize = cachedData.length * 2; // Rough UTF-16 estimate
				hasStorageCache = true;
			}
		} catch (error) {
			// localStorage not available
		}

		return {
			hasMemoryCache,
			hasStorageCache,
			storageSize,
			lastUpdated: memoryCached ? new Date(memoryCached.timestamp) : null,
			userSession: memoryCached ? memoryCached.userSession : null,
			isExpired: memoryCached ? !this.isCacheValid(memoryCached) : true,
			cacheVersion: this.CURRENT_CACHE_VERSION,
			memoryOnly: hasMemoryCache && !hasStorageCache, // ✅ NEW: Indicates storage failed
		};
	}

	// ==================== EVENT LISTENERS ====================

	onCacheChange(callback) {
		this.cacheListeners.push(callback);
		return () => {
			this.cacheListeners = this.cacheListeners.filter((cb) => cb !== callback);
		};
	}

	notifyListeners(event, data) {
		this.cacheListeners.forEach((callback) => {
			try {
				callback(event, data);
			} catch (error) {
				console.error("❌ Cache listener error:", error);
			}
		});
	}

	// ==================== UTILITIES ====================

	async preloadFilters(fetchFunction) {
		console.log("🚀 Preloading filters...");
		return await this.getFilters(fetchFunction);
	}

	exportCache() {
		const cacheData = this.memoryCache.get("filters");
		if (cacheData) {
			return {
				...cacheData,
				exportedAt: new Date().toISOString(),
			};
		}
		return null;
	}

	importCache(importedData) {
		try {
			if (importedData && importedData.data) {
				return this.setFilters(importedData.data);
			}
			return false;
		} catch (error) {
			console.error("❌ Error importing cache:", error);
			return false;
		}
	}
}

// Export singleton instance
const filterCacheManager = new FilterCacheManager();

// ✅ IMPORTANT: Make available globally for console testing
if (typeof window !== "undefined") {
	window.filterCacheManager = filterCacheManager;
}

export default filterCacheManager;
